/** SH24: inclusive point ranges, normalized units, checkbox-based Shopify filtering.
 * Checkbox names/values remain unchanged. facets.js receives one input event
 * after the entire selection has been updated. Rehydrates after AJAX replacement.
 */
(() => {
  'use strict';
  if (window.__sh24RangeSliderV2) return;
  window.__sh24RangeSliderV2 = true;

  function parseNumber(raw) {
    // Decimal comma and decimal point; reject ambiguous multi-separator input.
    if (!/^\d+(?:[.,]\d+)?$/.test(raw)) return NaN;
    return Number(raw.replace(',', '.'));
  }

  function unitInfo(raw) {
    const unit = raw.trim().toLowerCase();
    const power = { w: 1, wp: 1, kw: 1000, kwp: 1000, mw: 1000000, mwp: 1000000 };
    const energy = { wh: 1, kwh: 1000, mwh: 1000000 };
    if (Object.hasOwn(power, unit)) return { family: 'power', factor: power[unit] };
    if (Object.hasOwn(energy, unit)) return { family: 'energy', factor: energy[unit] };
    return { family: unit, factor: 1 };
  }

  function parseLabel(text) {
    const point = /^(\d+(?:[.,]\d+)?)\s*([^\d.,–—-]*)$/u;
    const range = /^(\d+(?:[.,]\d+)?)\s*([^\d.,–—-]*?)\s*[-–—]\s*(\d+(?:[.,]\d+)?)\s*([^\d.,–—-]*)$/u;
    const r = text.trim().match(range);
    const p = r ? null : text.trim().match(point);
    if (!r && !p) return null;
    const lowRaw = r ? r[1] : p[1];
    const highRaw = r ? r[3] : p[1];
    const lowUnit = r ? (r[2].trim() || r[4].trim()) : p[2].trim();
    const highUnit = r ? (r[4].trim() || r[2].trim()) : p[2].trim();
    const a = unitInfo(lowUnit), b = unitInfo(highUnit);
    const min = parseNumber(lowRaw) * a.factor;
    const max = parseNumber(highRaw) * b.factor;
    if (a.family !== b.family || !Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
    return { min, max, family: a.family, minLabel: lowRaw + lowUnit, maxLabel: highRaw + highUnit };
  }

  function parseBuckets(filter) {
    const inputs = [...filter.querySelectorAll('input[type="checkbox"]')];
    if (inputs.length < 2) return null;
    const buckets = inputs.map(checkbox => {
      const text = checkbox.dataset.rangeLabel ?? checkbox.closest('label')?.querySelector('.checkbox-facet-label-text')?.textContent.trim();
      const value = text && parseLabel(text);
      return value ? { ...value, checkbox } : null;
    });
    if (buckets.some(b => !b || b.family !== buckets[0].family)) return null;
    return buckets.sort((a, b) => a.min - b.min || a.max - b.max);
  }

  function makeScale(buckets) {
    const points = buckets.every(b => b.min === b.max);
    if (points) {
      const values = [...new Set(buckets.map(b => b.min))];
      const labels = values.map(v => buckets.find(b => b.min === v).minLabel);
      return { points, minValues: values, maxValues: values, minLabels: labels, maxLabels: labels, maxStep: values.length - 1, gap: 0 };
    }
    // Retain interval support only for non-overlapping, ordered buckets.
    // Mixed/overlapping intervals use the accurate checkbox fallback.
    if (buckets.some((b, i) => b.min === b.max || (i && b.min < buckets[i - 1].max))) return null;
    return {
      points, gap: 1, maxStep: buckets.length,
      minValues: [...buckets.map(b => b.min), buckets.at(-1).max],
      maxValues: [buckets[0].min, ...buckets.map(b => b.max)],
      minLabels: [...buckets.map(b => b.minLabel), buckets.at(-1).maxLabel],
      maxLabels: [buckets[0].minLabel, ...buckets.map(b => b.maxLabel)]
    };
  }

  function initialSelection(buckets, scale) {
    const active = buckets.filter(b => b.checkbox.checked);
    if (!active.length) return [0, scale.maxStep];
    const low = Math.min(...active.map(b => b.min));
    const high = Math.max(...active.map(b => b.max));
    return [scale.minValues.indexOf(low), scale.maxValues.lastIndexOf(high)];
  }

  class RangeSliderFacet {
    constructor(filter, buckets, scale, wrap) {
      this.filter = filter;
      this.buckets = buckets;
      this.scale = scale;
      this.wrap = wrap;
      this.minInput = wrap.querySelector('.range-slider-input-min');
      this.maxInput = wrap.querySelector('.range-slider-input-max');
      [this.minIndex, this.maxIndex] = initialSelection(buckets, scale);
      for (const input of [this.minInput, this.maxInput]) {
        input.min = 0;
        input.max = scale.maxStep;
        input.step = 1;
        input.disabled = scale.maxStep === 0;
        input.addEventListener('input', event => {
          event.stopPropagation();
          this.readInputs(event.target);
          this.updateUI();
        });
        input.addEventListener('change', event => {
          event.stopPropagation();
          this.readInputs(event.target);
          this.updateUI();
          this.applySelection();
        });
      }
      const ticks = wrap.querySelectorAll('.range-slider-ticks span');
      ticks[0].textContent = scale.minLabels[0];
      ticks[1].textContent = scale.maxLabels[scale.maxStep];
      // Facet counts can overlap for multivalue products; do not sum them as products.
      const count = wrap.querySelector('.range-slider-count');
      if (count) count.hidden = true;
      this.updateUI();
      wrap.dataset.hydrated = 'true';
      wrap.hidden = false;
      wrap.removeAttribute('aria-busy');
      filter.dataset.rangeFilter = 'true';
    }

    readInputs(target) {
      const gap = this.scale.gap;
      let low = Number(this.minInput.value), high = Number(this.maxInput.value);
      if (target === this.minInput) low = Math.min(low, high - gap);
      else high = Math.max(high, low + gap);
      this.minIndex = Math.max(0, low);
      this.maxIndex = Math.min(this.scale.maxStep, high);
    }

    updateUI() {
      const s = this.scale;
      this.minInput.value = this.minIndex;
      this.maxInput.value = this.maxIndex;
      const low = s.minLabels[this.minIndex], high = s.maxLabels[this.maxIndex];
      this.wrap.querySelector('.range-slider-value-min').textContent = low;
      this.wrap.querySelector('.range-slider-value-max').textContent = high;
      this.minInput.setAttribute('aria-valuetext', low);
      this.maxInput.setAttribute('aria-valuetext', high);
      const fill = this.wrap.querySelector('.range-slider-track-fill');
      fill.style.left = `${s.maxStep ? this.minIndex / s.maxStep * 100 : 0}%`;
      fill.style.right = `${s.maxStep ? 100 - this.maxIndex / s.maxStep * 100 : 0}%`;
      // A URL may select isolated values. Initialization must never select the gaps.
      const active = this.buckets.filter(b => b.checkbox.checked);
      const hasGaps = active.length && this.buckets.some(b => this.contains(b) && !b.checkbox.checked);
      this.wrap.title = hasGaps ? 'Einzelwerte aktiv. Beim Verschieben werden alle Werte im gewählten Bereich ausgewählt.' : '';
    }

    contains(bucket) {
      return bucket.min >= this.scale.minValues[this.minIndex] && bucket.max <= this.scale.maxValues[this.maxIndex];
    }

    applySelection() {
      const full = this.minIndex === 0 && this.maxIndex === this.scale.maxStep;
      let trigger = null;
      this.buckets.forEach(b => {
        // Full extent removes this facet restriction. Other facets remain intact.
        const checked = !full && this.contains(b);
        if (b.checkbox.checked !== checked || (checked && b.checkbox.disabled)) {
          b.checkbox.checked = checked;
          // Zero-count values must be submitted as well; otherwise an empty range
          // becomes no filter at all. Shopify decides the actual matching products.
          if (checked) b.checkbox.disabled = false;
          trigger = b.checkbox;
        }
      });
      if (trigger) trigger.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function scan() {
    document.querySelectorAll('#FacetFiltersForm .js-filter').forEach(filter => {
      const list = filter.querySelector('.facet-options-list');
      const wrap = filter.querySelector('.range-slider');
      if (!list) return;
      if (!wrap) {
        delete filter.dataset.rangeFilter;
        return;
      }
      if (wrap.dataset.hydrated === 'true') return;
      const buckets = parseBuckets(filter);
      const scale = buckets && makeScale(buckets);
      if (!scale) {
        delete filter.dataset.rangeFilter;
        wrap.hidden = true;
        list.style.display = 'block';
        list.setAttribute('aria-expanded', 'true');
        return;
      }
      new RangeSliderFacet(filter, buckets, scale, wrap);
    });
  }

  function init() {
    scan();
    new MutationObserver(records => {
      if (records.some(r => [...r.addedNodes].some(n => n.nodeType === 1))) scan();
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
